import { Checkbox } from '@/components/ui/checkbox'
import { router } from '@inertiajs/react'
import { useState } from 'react'
import type {
  ProjectPlan,
  ProjectPlanCopy,
  ProjectPlanFiles,
} from '@/frontend/features/Fde/projectPlanTypes'
import type { ProjectTask } from '@/frontend/features/Fde/types'

interface Props {
  plan: ProjectPlan
  projectId: string
  fileDirectory?: ProjectPlanFiles
  copy: ProjectPlanCopy
  paths: string[]
  taskIds?: string[]
  tasks?: ProjectTask[]
  onPaths: (paths: string[]) => void
  onTasks?: (ids: string[]) => void
}

export default function ProjectPlanLinkPicker(props: Props) {
  const [loading, setLoading] = useState(false)
  const directory =
    props.fileDirectory?.project_id === props.projectId ? props.fileDirectory : undefined
  const files = directory?.files ?? props.plan.files
  function loadFiles(open: boolean) {
    if (!open || directory || loading) return
    setLoading(true)
    router.reload({ only: ['project_plan_files'], onFinish: () => setLoading(false) })
  }
  const toggle = (values: string[], value: string, checked: boolean) =>
    checked ? [...values, value] : values.filter((item) => item !== value)
  return (
    <details
      className="rounded-md border border-border text-sm"
      onToggle={(event) => loadFiles(event.currentTarget.open)}
    >
      <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground">
        {props.tasks ? props.copy.links : props.copy.sources}
      </summary>
      <div className="max-h-56 space-y-4 overflow-auto px-3 pb-3">
        {props.tasks && (
          <div className="space-y-2">
            <p className="text-xs font-medium">{props.copy.delivery_tasks}</p>
            {props.tasks.map((task) => (
              <label key={task.id} className="flex items-start gap-2 text-xs">
                <Checkbox
                  checked={props.taskIds?.includes(task.id)}
                  onCheckedChange={(checked) =>
                    props.onTasks?.(toggle(props.taskIds ?? [], task.id, checked === true))
                  }
                />
                <span>
                  {task.identifier} · {task.title}
                </span>
              </label>
            ))}
          </div>
        )}
        <div className="space-y-2">
          <p className="text-xs font-medium">{props.copy.delivery_files}</p>
          {files.map((file) => (
            <label key={file.path} className="flex items-start gap-2 text-xs">
              <Checkbox
                checked={props.paths.includes(file.path)}
                onCheckedChange={(checked) =>
                  props.onPaths(toggle(props.paths, file.path, checked === true))
                }
              />
              <span title={file.path}>{file.title}</span>
            </label>
          ))}
          {loading && <p className="text-xs text-muted-foreground">{props.copy.loading_files}</p>}
          {directory?.error && (
            <p role="alert" className="text-xs text-destructive">
              {directory.error}
            </p>
          )}
          {!files.length && !loading && !directory?.error && (
            <p className="text-xs leading-5 text-muted-foreground">{props.copy.no_sources}</p>
          )}
        </div>
      </div>
    </details>
  )
}
